<template>
  <canvas ref="canvas" />
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed } from 'vue';

import LineChart from '@/vendor/chartjs/line_chart';

import { screenAtLeast } from '@/functions/screen_width';
import { formatDate } from '@/functions/date_format_helpers';

const props = defineProps({
  task: { type: Object, required: true },
});

// Sets the default failure limit to 0, if no value was specified.
const failureLimit = computed(() => {
  return Number(props.task.values.failure_value) || 0;
});

const canvas = ref(null);
let chart;

onMounted(() => {
  renderLineChart();
  window.addEventListener('eaimms:breakpoint.change', updateChartOptions, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('eaimms:breakpoint.change', updateChartOptions);
});

function renderLineChart() {
  if (props.task.task_results.length === 0) return;

  chart = new LineChart(
    canvas.value,
    formattedSeries(),
    captureDates(),
    chartOptions(),
  );
}

function updateChartOptions() {
  chart.update(chartOptions());
}

/** Dates, used as the labels for the chart. */
function captureDates()  {
  return props.task.task_results.map(tr => formatDate(tr.captured_at));
}

/** Format task results values */
function taskResultsSeries() {
  return props.task.task_results.map(tr => parseFloat(tr.data.value));
}

/** Create a series for the failure value, by adding the value for every date captured. */
function failureValueSeries() {
  return Array
    .from(captureDates())
    .fill(failureLimit.value);
}

/** Format the series data with their line configurations. */
function formattedSeries() {
  // main task results series
  let series = [{
    values: taskResultsSeries(),
    options: {
      borderColor: '#29A7F4',
      borderWidth: 2,
      pointRadius: 1,
    }
  }];

  series.push({
    values: failureValueSeries(),
    options: {
      borderColor: '#E54666',
      borderWidth: 1.5,
      pointRadius: 0,
    }
  });

  return series;
}

/** Override the axis label color, and make the failure value the only visible tick label. */
function chartOptions() {

  if (screenAtLeast('medium')) return { type: 'large' };

  let options = {
    overrideTicks: [failureValueSeries()[0]],
    axisLabelColor: '#E54666'
  };

  options['overrideTicks'] = [failureValueSeries()[0]];
  options['axisLabelColor'] = '#E54666';

  return options;
}
</script>
